"use client";

/**
 * RightRailResizer — RightRail 과 main content 사이 드래그 핸들 (사용자 결정 2026-05-17).
 *
 * 드래그 시 RightRail width 조절:
 *   - min 240px, max 560px, default 320px
 *   - localStorage 영속: bs.right-rail-width
 *   - mousemove + touchmove 모두 지원
 *
 * RightRail 의 width 와 AppShell main 의 padding-right 를 동시에 inline style 로 변경
 * (CSS var --bs-rr-width 통해).
 */

import { useEffect, useRef, useState } from "react";

const STORAGE_KEY = "bs.right-rail-width";
const MIN_W = 240;
const MAX_W = 560;
const DEFAULT_W = 320;

export function RightRailResizer() {
  const [width, setWidth] = useState(DEFAULT_W);
  const [dragging, setDragging] = useState(false);
  const draggingRef = useRef(false);

  // localStorage 복원 (hydration safe — useEffect 안)
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const v = parseInt(raw, 10);
        if (Number.isFinite(v) && v >= MIN_W && v <= MAX_W) setWidth(v);
      }
    } catch {}
  }, []);

  // width 변경 시 CSS var 적용
  useEffect(() => {
    document.documentElement.style.setProperty("--bs-rr-width", `${width}px`);
  }, [width]);

  // 드래그 — pointer events
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!draggingRef.current) return;
      const newW = Math.min(MAX_W, Math.max(MIN_W, window.innerWidth - e.clientX));
      setWidth(newW);
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setDragging(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      try {
        window.localStorage.setItem(STORAGE_KEY, String(width));
      } catch {}
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [width]);

  const onDown = (e: React.PointerEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    setDragging(true);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
  };

  // 미니멀 — 평소 1px gray-200 divider, hover/drag 시 2px red. dots 폐기 (사용자: "너무 개좆구리").
  // 넓은 hit area (8px) — 드래그 잡기 쉽게, 시각 표시는 가운데 얇은 라인만.
  return (
    <div
      role="separator"
      aria-label="우측 패널 폭 조절"
      aria-orientation="vertical"
      aria-valuemin={MIN_W}
      aria-valuemax={MAX_W}
      aria-valuenow={width}
      onPointerDown={onDown}
      className="group fixed top-0 z-30 h-screen w-2 cursor-ew-resize max-[1100px]:hidden flex items-center justify-center"
      style={{
        right: `calc(var(--bs-rr-width, ${DEFAULT_W}px) - 4px)`, // hit area 가운데 정렬
      }}
    >
      <div
        className="h-full transition-all duration-150"
        style={{
          width: dragging ? 2 : 1,
          background: dragging ? "var(--bs-red-500)" : "var(--bs-gray-200)",
        }}
      />
      {/* hover 시 라인이 진해짐 (Tailwind group-hover 로) */}
      <style jsx>{`
        .group:hover > div {
          width: 2px;
          background: var(--bs-gray-500);
        }
      `}</style>
    </div>
  );
}
